import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { Background, Controls, MarkerType, MiniMap, ReactFlow, ReactFlowProvider, useNodesState, useReactFlow, useStore, type Connection, type Edge, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type KeyboardEvent, type SetStateAction } from 'react';
import { Button, ErrorState } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasDefinition, type CanvasDraftView, type CanvasSimulationResult, type CanvasStepTypeView, type CanvasValidation } from '../../../api/client.ts';
import { usePermissions } from '../../access/usePermissions.ts';
import { useStepCommentCounts } from '../../comments/CommentsSidebar.tsx';
import { EditorShell } from '../../editor/EditorShell.tsx';
import { sameContent } from '../../editor/normalize.ts';
import {
  addStep,
  addVariant,
  cloneVariant,
  connect,
  copyFragment,
  deleteStep,
  deleteVariant,
  duplicateStep,
  moveNodes,
  newNodeId,
  pasteFragment,
  renameStep,
  renameVariant,
  setStepConfig,
  setVariantPercent,
  variantKey,
  type Draft,
  type Fragment,
  type Position,
  type Where,
} from './commands.ts';
import { record, redo, startHistory, undo, type History } from './history.ts';
import { autoLayout } from './layout.ts';
import { EditorActionsContext, LinkEdge, StepNode, VariantNode, type EditorActions, type LinkEdgeType, type StepNodeType, type VariantNodeType } from './nodes.tsx';
import { Palette } from './Palette.tsx';
import { StepInspector } from './StepInspector.tsx';
import { isOrderLocked, portsOf } from './steps/ports.ts';
import { SimulatorPanel } from '../simulator/SimulatorPanel.tsx';

const nodeTypes = { step: StepNode, variant: VariantNode };
const edgeTypes = { link: LinkEdge };
const isVariant = (id: string) => id.startsWith('variant__');
const variantOf = (id: string) => id.slice('variant__'.length);
const clipboardKey = (environmentId: string) => `bb.canvas.clipboard.${environmentId}`;
const ZOOM_STEPS = [0.1, 0.25, 0.5, 0.75, 1] as const;

/** Braze-style zoom selector (10–100 %); shows the current zoom. */
function ZoomSelect() {
  const flow = useReactFlow();
  const zoom = useStore((s) => s.transform[2]);
  const current = ZOOM_STEPS.reduce((best, z) => (Math.abs(z - zoom) < Math.abs(best - zoom) ? z : best), 1);
  return (
    <label className="hint">
      Zoom{' '}
      <select aria-label="Zoom" value={String(current)} onChange={(e) => void flow.zoomTo(Number(e.target.value), { duration: 150 })}>
        {ZOOM_STEPS.map((z) => (
          <option key={z} value={String(z)}>
            {Math.round(z * 100)}%
          </option>
        ))}
      </select>
    </label>
  );
}

/** `31 days 5 s` from seconds (the server's max duration). */
export const duration = (seconds: number) => {
  const parts: string[] = [];
  let left = seconds;
  for (const [size, one, many] of [
    [86_400, 'day', 'days'],
    [3_600, 'h', 'h'],
    [60, 'min', 'min'],
    [1, 's', 's'],
  ] as const) {
    const n = Math.floor(left / size);
    if (n) parts.push(`${n} ${n === 1 ? one : many}`);
    left -= n * size;
  }
  return parts.join(' ') || '0 s';
};

/** The editor's graph: undo history, what was saved last and the save itself; shared by the editor page and the wizard (BB-110). */
export interface CanvasGraph {
  history: History;
  setHistory: Dispatch<SetStateAction<History>>;
  saved: { definition: CanvasDefinition; updatedAt: string };
  /** The graph on screen differs from the saved one. */
  dirty: boolean;
  save: UseMutationResult<CanvasDraftView, Error, void>;
  /** Another save bumped the Canvas (entry settings): the next graph save compares with this draft's time stamp. */
  rebase: (draft: CanvasDraftView) => void;
}

/** State of the draft graph being edited; a save also refreshes the cached draft, so reopening shows what was saved. */
export function useCanvasGraph(api: AdminApi, environmentId: string, canvasId: string, initial: CanvasDraftView): CanvasGraph {
  const queryClient = useQueryClient();
  const [history, setHistory] = useState<History>(() => startHistory(initial.definition));
  const [saved, setSaved] = useState({ definition: initial.definition, updatedAt: initial.updatedAt });
  const draftKey = ['canvases', environmentId, 'draft', canvasId];
  const save = useMutation({
    mutationFn: () => api.saveCanvasDraft(environmentId, canvasId, { definition: history.present, baseUpdatedAt: saved.updatedAt }),
    onSuccess: (result) => {
      setSaved({ definition: result.definition, updatedAt: result.updatedAt });
      queryClient.setQueryData(draftKey, result);
    },
  });
  const rebase = useCallback(
    (draft: CanvasDraftView) => {
      setSaved((current) => ({ ...current, updatedAt: draft.updatedAt }));
      queryClient.setQueryData(['canvases', environmentId, 'draft', canvasId], draft);
    },
    [queryClient, environmentId, canvasId],
  );
  return { history, setHistory, saved, dirty: !sameContent(history.present, saved.definition), save, rebase };
}

/** Messaging → Canvas → Edit: the journey graph editor (BB-114). */
export function CanvasEditorPage() {
  const { environmentId, canvasId } = useParams({ from: '/env/$environmentId/canvas/$canvasId/edit' });
  const { api } = useRouteContext({ from: '/env/$environmentId/canvas/$canvasId/edit' });
  const canvas = useQuery({ queryKey: ['canvases', environmentId, 'one', canvasId], queryFn: () => api.getCanvas(environmentId, canvasId) });
  const draft = useQuery({ queryKey: ['canvases', environmentId, 'draft', canvasId], queryFn: () => api.canvasDraft(environmentId, canvasId), staleTime: Infinity });
  const types = useQuery({ queryKey: ['canvas-step-types', environmentId], queryFn: () => api.canvasStepTypes(environmentId), staleTime: Infinity });
  const canEdit = usePermissions().has('canvas.edit');
  const failed = canvas.error ?? draft.error ?? types.error;
  if (failed) return <ErrorState title="The Canvas editor could not be loaded" message={failed instanceof ApiError ? failed.message : 'Something went wrong.'} />;
  if (!canvas.data || !draft.data || !types.data) return <p aria-busy="true">Loading the editor…</p>;
  const readOnly = !canEdit || canvas.data.status === 'archived';
  return (
    <ReactFlowProvider>
      <StandaloneEditor
        api={api}
        environmentId={environmentId}
        canvasId={canvasId}
        name={canvas.data.name}
        initial={draft.data}
        types={types.data.items}
        readOnly={readOnly}
        readOnlyReason={canvasReadOnlyReason(canEdit, canvas.data.status)}
        canPreview={canEdit}
      />
    </ReactFlowProvider>
  );
}

/** Why the Canvas cannot be changed by this employee now, if it cannot. */
export const canvasReadOnlyReason = (canEdit: boolean, status: string): string | undefined =>
  !canEdit ? 'Viewing only: changing the Canvas needs the canvas.edit permission.' : status === 'archived' ? 'The Canvas is archived: restore it to change it.' : undefined;

/** The editor on its own page: leaving with unsaved changes asks (BB-20). */
function StandaloneEditor(props: Omit<CanvasGraphEditorProps, 'graph' | 'embedded'>) {
  const graph = useCanvasGraph(props.api, props.environmentId, props.canvasId, props.initial);
  return (
    <EditorShell dirty={graph.dirty} onSave={() => graph.save.mutateAsync()} canSave={!props.readOnly} objectLabel="Canvas">
      <CanvasGraphEditor {...props} graph={graph} />
    </EditorShell>
  );
}

export interface CanvasGraphEditorProps {
  api: AdminApi;
  environmentId: string;
  canvasId: string;
  name: string;
  /** The draft as loaded; its running version gives the locks of paths steps. */
  initial: CanvasDraftView;
  types: CanvasStepTypeView[];
  readOnly: boolean;
  readOnlyReason?: string | undefined;
  /** Previews that need `canvas.edit` (Delay wake-up) may be asked for. */
  canPreview: boolean;
  graph: CanvasGraph;
  /** Inside the Canvas wizard (BB-110): the wizard saves and shows save errors, so no back link and no «Save draft». */
  embedded?: boolean;
}

/** React Flow editor of the draft graph (BB-114); render it inside a `ReactFlowProvider` and an `EditorShell`. */
export function CanvasGraphEditor({ api, environmentId, canvasId, name, initial, types, readOnly, readOnlyReason, canPreview, graph, embedded = false }: CanvasGraphEditorProps) {
  const flow = useReactFlow();
  const { history, setHistory, dirty, save } = graph;
  const [insertion, setInsertion] = useState<Where | null>(null);
  const [detailed, setDetailed] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [validation, setValidation] = useState<CanvasValidation | null>(null);
  const [auto, setAuto] = useState<Record<string, Position>>({});
  const [testing, setTesting] = useState(false);
  /** The last Test Canvas run (BB-122): its path is highlighted on the graph. */
  const [simulation, setSimulation] = useState<CanvasSimulationResult | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const draft = history.present;
  const comments = useStepCommentCounts(environmentId, canvasId);
  const apply = useCallback((change: (d: Draft) => Draft) => setHistory((h) => record(h, change(h.present))), [setHistory]);
  /** A step's settings from the inspector: its ports follow them; quick edits of one step are one undo step. */
  const editConfig = (stepId: string, config: unknown) => {
    const at = Date.now();
    setHistory((h) => {
      const step = h.present.steps[stepId];
      return step ? record(h, setStepConfig(h.present, stepId, config, portsOf(step.type, config)), { key: `config:${stepId}`, at }) : h;
    });
  };
  const typeOf = (type: string) => types.find((t) => t.type === type);

  // Validation of what is on screen, a moment after the last change.
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      api.validateCanvasDraft(environmentId, canvasId, draft).then(
        (result) => live && setValidation(result),
        () => live && setValidation(null),
      );
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [api, environmentId, canvasId, draft]);

  // Nodes without a stored position get an automatic one (not a change of the draft until «Clean Up» or a drag).
  const nodeKeys = [...draft.variants.map((v) => variantKey(v.id)), ...Object.keys(draft.steps)];
  const missing = nodeKeys.filter((k) => !draft.layout?.[k]).join('|');
  useEffect(() => {
    if (!missing) return;
    let live = true;
    void autoLayout(draft).then((positions) => live && setAuto(positions));
    return () => {
      live = false;
    };
    // Recomputed only when the set of unplaced nodes changes.
  }, [missing]);

  const position = (key: string, index: number): Position => draft.layout?.[key] ?? auto[key] ?? { x: index * 260, y: 0 };
  const simPath = simulation?.outcome.path ?? [];
  const simClass = (stepId: string) => (simPath.includes(stepId) ? `canvas-node--sim-path${stepId === simulation?.outcome.stepId ? ' canvas-node--sim-end' : ''}` : undefined);
  const derivedNodes = useMemo<Array<StepNodeType | VariantNodeType>>(
    () => [
      ...draft.variants.map((v, i): VariantNodeType => ({ id: variantKey(v.id), type: 'variant', position: position(variantKey(v.id), i), data: { id: v.id, name: v.name, percent: v.percent, canDelete: draft.variants.length > 1 }, deletable: !readOnly && draft.variants.length > 1 })),
      ...Object.values(draft.steps).map(
        (s, i): StepNodeType => ({
          id: s.id,
          type: 'step',
          position: position(s.id, i),
          data: { step: s, typeLabel: typeOf(s.type)?.label ?? s.type, summary: validation?.summaries[s.id], issues: validation?.issues.filter((x) => x.stepId === s.id) ?? [], comments: comments[s.id] ?? 0 },
          deletable: !readOnly,
          ...(simClass(s.id) ? { className: simClass(s.id) } : {}),
        }),
      ),
    ],
    [draft, auto, validation, comments, readOnly, types, simulation],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<StepNodeType | VariantNodeType>(derivedNodes);
  useEffect(() => {
    // Keep what React Flow tracks (selection) when the draft changes.
    setNodes((current) => derivedNodes.map((n) => ({ ...n, selected: current.find((c) => c.id === n.id)?.selected ?? false })) as Array<StepNodeType | VariantNodeType>);
  }, [derivedNodes, setNodes]);

  /** An edge the simulated user took: consecutive steps of the path. */
  const onSimPath = (from: string, to: string) => simPath.some((id, i) => id === from && simPath[i + 1] === to);
  const edges = useMemo<LinkEdgeType[]>(
    () => [
      ...draft.variants
        .filter((v) => v.startStepId && draft.steps[v.startStepId])
        .map((v): LinkEdgeType => ({ id: `${variantKey(v.id)}->${v.startStepId}`, source: variantKey(v.id), sourceHandle: 'start', target: v.startStepId!, type: 'link', markerEnd: { type: MarkerType.ArrowClosed }, data: { where: { kind: 'after_variant', variantId: v.id }, label: '', from: v.name }, deletable: !readOnly })),
      ...Object.values(draft.steps).flatMap((s) =>
        s.next
          .filter((n) => n.stepId && draft.steps[n.stepId])
          .map((n): LinkEdgeType => ({ id: `${s.id}:${n.port}->${n.stepId}`, source: s.id, sourceHandle: n.port, target: n.stepId!, type: 'link', markerEnd: { type: MarkerType.ArrowClosed }, data: { where: { kind: 'after_step', stepId: s.id, port: n.port }, label: s.next.length > 1 ? n.port : '', from: s.name }, deletable: !readOnly, ...(onSimPath(s.id, n.stepId!) ? { className: 'canvas-edge--sim-path', animated: true } : {}) })),
      ),
    ],
    [draft, readOnly, simulation],
  );

  const flowCenter = (): Position => {
    const box = container.current?.getBoundingClientRect();
    return box ? flow.screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 3 }) : { x: 0, y: 0 };
  };
  const addOfType = (type: CanvasStepTypeView) => {
    const id = newNodeId('s');
    apply((d) => addStep(d, type, insertion ?? { kind: 'free', position: flowCenter() }, id));
    setInsertion(null);
  };
  const copySelection = () => {
    const ids = nodes.filter((n) => n.selected && !isVariant(n.id)).map((n) => n.id);
    if (ids.length === 0) return;
    const fragment = copyFragment(draft, ids);
    try {
      localStorage.setItem(clipboardKey(environmentId), JSON.stringify(fragment));
    } catch {
      // Storage may be unavailable; copying then stays a no-op.
    }
    setNotice(`Copied ${ids.length} ${ids.length === 1 ? 'step' : 'steps'}`);
  };
  const paste = () => {
    let fragment: Fragment | null = null;
    try {
      fragment = JSON.parse(localStorage.getItem(clipboardKey(environmentId)) ?? 'null') as Fragment | null;
    } catch {
      fragment = null;
    }
    if (fragment?.steps?.length) apply((d) => pasteFragment(d, fragment!, newNodeId));
  };

  const actions: EditorActions = {
    readOnly,
    detailed,
    insertion,
    pickInsert: setInsertion,
    renameStep: (id, value) => apply((d) => renameStep(d, id, value)),
    duplicateStep: (id) => apply((d) => duplicateStep(d, id, newNodeId('s'))),
    deleteStep: (id) => {
      apply((d) => deleteStep(d, id));
      setInsertion(null);
    },
    copyStepId: (id) => {
      void navigator.clipboard?.writeText(id).catch(() => undefined);
      setNotice(`Step ID ${id} copied`);
    },
    renameVariant: (id, value) => apply((d) => renameVariant(d, id, value)),
    setVariantPercent: (id, percent) => apply((d) => setVariantPercent(d, id, percent)),
    cloneVariant: (id) => apply((d) => cloneVariant(d, id, newNodeId)),
    deleteVariant: (id) => apply((d) => deleteVariant(d, id)),
  };

  const saveDraft = () => save.mutate(undefined, { onSuccess: () => setNotice('Saved') });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select') || readOnly) return;
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) setHistory(undo);
    else if ((key === 'z' && e.shiftKey) || key === 'y') setHistory(redo);
    else if (key === 'c') copySelection();
    else if (key === 'v') paste();
    else return;
    e.preventDefault();
  };

  // The graph's own problems; entry, audience and controls belong to the wizard's steps (BB-110).
  const graphIssues = validation?.issues.filter((i) => !i.section || i.section === 'build') ?? [];
  const errors = graphIssues.filter((i) => i.severity === 'error');
  const warnings = graphIssues.filter((i) => i.severity === 'warning');
  const insertionLabel = (() => {
    if (!insertion || insertion.kind === 'free') return 'Pick a step to add it to the Canvas, or choose a «+» or a port first.';
    if (insertion.kind === 'after_variant') return `Adds at the start of ${draft.variants.find((v) => v.id === insertion.variantId)?.name ?? 'the variant'}.`;
    return `Adds after «${draft.steps[insertion.stepId]?.name ?? insertion.stepId}» · ${insertion.port}.`;
  })();
  const selected = nodes.filter((n) => n.selected);
  const inspected = selected.length === 1 && !isVariant(selected[0]!.id) ? draft.steps[selected[0]!.id] : undefined;
  const published = initial.published?.definition ?? null;
  const closeInspector = () => setNodes((current) => current.map((n) => (n.selected ? { ...n, selected: false } : n)));
  const focusStep = (stepId: string) => {
    const node = nodes.find((n) => n.id === stepId);
    if (!node) return;
    void flow.setCenter(node.position.x + 120, node.position.y + 40, { zoom: 1, duration: 200 });
    setNodes((current) => current.map((n) => ({ ...n, selected: n.id === stepId })));
  };

  return (
    <section className="canvas-editor" aria-label={`Canvas editor: ${name}`}>
      <header className="canvas-editor__toolbar" role="toolbar" aria-label="Canvas editor tools">
        {!embedded && (
          <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId }}>
            ← {name}
          </Link>
        )}
        <Button variant="ghost" disabled={readOnly || history.past.length === 0} onClick={() => setHistory(undo)}>
          Undo
        </Button>
        <Button variant="ghost" disabled={readOnly || history.future.length === 0} onClick={() => setHistory(redo)}>
          Redo
        </Button>
        <Button variant="ghost" disabled={readOnly} onClick={() => apply((d) => addVariant(d, newNodeId('v')))}>
          Add Variant
        </Button>
        <Button
          variant="ghost"
          disabled={readOnly}
          onClick={() =>
            void autoLayout(draft).then((positions) => {
              apply((d) => moveNodes(d, positions));
              void flow.fitView({ duration: 200 });
            })
          }
        >
          Clean Up Canvas
        </Button>
        <Button variant="ghost" aria-pressed={detailed} onClick={() => setDetailed((v) => !v)}>
          {detailed ? 'Detailed view' : 'Simplified view'}
        </Button>
        <Button variant="ghost" onClick={() => void flow.fitView({ duration: 200, minZoom: 0.1, maxZoom: 1 })}>
          Fit
        </Button>
        <ZoomSelect />
        <Button variant="ghost" onClick={() => void container.current?.requestFullscreen?.().catch(() => undefined)}>
          Fullscreen
        </Button>
        <span role="status" className={errors.length ? 'field-error' : 'hint'}>
          {validation ? (errors.length ? `${errors.length} ${errors.length === 1 ? 'problem' : 'problems'}` : 'Valid') : 'Checking…'}
          {validation?.maxDurationSeconds != null ? ` · max duration ${duration(validation.maxDurationSeconds)}` : ''}
        </span>
        {canPreview && !embedded && (
          <Button
            variant="ghost"
            aria-pressed={testing}
            onClick={() => {
              setTesting((v) => !v);
              setSimulation(null);
            }}
          >
            Test Canvas
          </Button>
        )}
        {!readOnly && !embedded && (
          <Button variant="primary" disabled={!dirty} loading={save.isPending} onClick={saveDraft}>
            Save draft
          </Button>
        )}
      </header>
      {save.isError && !embedded && <p role="alert">{save.error instanceof ApiError && save.error.code === 'revision_conflict' ? 'Someone else saved this Canvas meanwhile. Reload to see their version; your changes stay until you leave.' : save.error instanceof ApiError ? save.error.message : 'The draft could not be saved.'}</p>}
      {notice && (
        <p className="hint" aria-live="polite">
          {notice}
        </p>
      )}
      <div className={['canvas-editor__body', readOnly ? 'canvas-editor__body--no-palette' : '', inspected || testing ? 'canvas-editor__body--inspecting' : ''].filter(Boolean).join(' ')}>
        {!readOnly && <Palette types={types} target={insertionLabel} disabled={readOnly} onPick={addOfType} />}
        <div ref={container} className="canvas-editor__flow" tabIndex={-1} onKeyDown={onKeyDown}>
          <EditorActionsContext.Provider value={actions}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={onNodesChange}
              onNodeDragStop={(_e, _node, dragged) => apply((d) => moveNodes(d, Object.fromEntries(dragged.map((n) => [n.id, n.position]))))}
              onConnect={(c: Connection) => {
                if (!c.target || isVariant(c.target)) return;
                apply((d) => (isVariant(c.source) ? connect(d, { variantId: variantOf(c.source) }, c.target) : connect(d, { stepId: c.source, port: c.sourceHandle ?? '' }, c.target)));
              }}
              onEdgesDelete={(removed: Edge[]) => apply((d) => removed.reduce((acc, e) => (isVariant(e.source) ? connect(acc, { variantId: variantOf(e.source) }, null) : connect(acc, { stepId: e.source, port: e.sourceHandle ?? '' }, null)), d))}
              onNodesDelete={(removed: Node[]) => apply((d) => removed.reduce((acc, n) => (isVariant(n.id) ? deleteVariant(acc, variantOf(n.id)) : deleteStep(acc, n.id)), d))}
              nodesDraggable={!readOnly}
              nodesConnectable={!readOnly}
              elementsSelectable
              deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
              minZoom={0.1}
              maxZoom={1.5}
              fitView
              fitViewOptions={{ minZoom: 0.5, maxZoom: 1 }}
              proOptions={{ hideAttribution: true }}
            >
              <Background />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable ariaLabel="Canvas overview" />
            </ReactFlow>
          </EditorActionsContext.Provider>
        </div>
        <div className="canvas-editor__side">
          {inspected && (
            <StepInspector
              key={inspected.id}
              environmentId={environmentId}
              step={inspected}
              steps={draft.steps}
              typeLabel={typeOf(inspected.type)?.label ?? inspected.type}
              runtime={typeOf(inspected.type)?.runtime ?? false}
              issues={validation?.issues.filter((i) => i.stepId === inspected.id) ?? []}
              summary={validation?.summaries[inspected.id]}
              readOnly={readOnly}
              readOnlyReason={readOnlyReason}
              locked={isOrderLocked(published, inspected.id, inspected.type)}
              launched={published?.steps[inspected.id]?.type === inspected.type}
              canPreview={canPreview}
              onConfigChange={(config) => editConfig(inspected.id, config)}
              onRename={(value) => apply((d) => renameStep(d, inspected.id, value))}
              onClose={closeInspector}
            />
          )}
          {testing && (
            <SimulatorPanel
              api={api}
              environmentId={environmentId}
              canvasId={canvasId}
              draft={draft}
              dirty={dirty}
              onSave={readOnly ? undefined : () => save.mutateAsync()}
              saving={save.isPending}
              published={published}
              onResult={setSimulation}
              onFocusStep={focusStep}
              onClose={() => {
                setTesting(false);
                setSimulation(null);
              }}
            />
          )}
          <aside className="canvas-editor__issues" aria-label="Problems">
            <h3>Problems</h3>
            {validation && errors.length === 0 && warnings.length === 0 && <p className="hint">None.</p>}
            <ul>
              {[...errors, ...warnings].map((issue, i) => (
                <li key={`${issue.code}-${issue.stepId ?? ''}-${i}`} className={issue.severity === 'error' ? 'field-error' : 'hint'}>
                  {issue.message}
                  {issue.stepId && draft.steps[issue.stepId] && (
                    <>
                      {' '}
                      <button type="button" className="bb-button bb-button--ghost" onClick={() => focusStep(issue.stepId!)}>
                        Show «{draft.steps[issue.stepId]!.name}»
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}
